import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { codingAdminService } from '@/services/coding/codingAdminService';
import type { CodingProblemAdminData } from '@/services/coding/codingAdminService';
import CodingProblemModal from './CodingProblemModal';
import Button from '@components/Button/Button';
import Toggle from '@components/common/Toggle';
import Modal from '@components/common/Modal/Modal';
import {
  Plus,
  Edit2,
  Trash2,
  Code2,
  CheckCircle,
  Users,
  Layers,
  Calendar,
  Search,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Copy,
  Check,
  Terminal,
  ChevronDown,
  ChevronUp,
  RotateCcw,
} from 'lucide-react';

interface CodingTasksTabProps {
  cohortId: string;
}

const CodingTasksTab: React.FC<CodingTasksTabProps> = ({ cohortId }) => {
  const queryClient = useQueryClient();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState<number | 'ALL'>('ALL');
  const [editingProblem, setEditingProblem] = useState<CodingProblemAdminData | null>(null);
  const [viewingSubmissionsProblem, setViewingSubmissionsProblem] = useState<CodingProblemAdminData | null>(null);

  // Submissions modal state
  const [submissionSearch, setSubmissionSearch] = useState('');
  const [submissionStatusFilter, setSubmissionStatusFilter] = useState<'ALL' | 'ACCEPTED' | 'WRONG_ANSWER' | 'RUNTIME_ERROR' | 'TIME_LIMIT_EXCEEDED'>('ALL');
  const [expandedSubmissionId, setExpandedSubmissionId] = useState<string | null>(null);
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);

  // Fetch problems
  const { data: response, isLoading } = useQuery({
    queryKey: ['cohort-coding-problems', cohortId],
    queryFn: () => codingAdminService.getCohortProblems(cohortId),
  });

  const problems: CodingProblemAdminData[] = response?.data?.data || [];

  // Active weeks with tasks
  const activeWeeks = useMemo(() => {
    const set = new Set<number>();
    problems.forEach((p) => {
      set.add(p.week_number || 1);
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [problems]);

  // Filter problems by week
  const filteredProblems = useMemo(() => {
    if (selectedWeek === 'ALL') return problems;
    return problems.filter((p) => (p.week_number || 1) === selectedWeek);
  }, [problems, selectedWeek]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (problemId: string) => codingAdminService.deleteProblem(problemId),
    onSuccess: () => {
      toast.success('Coding problem deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['cohort-coding-problems', cohortId] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to delete problem');
    },
  });

  // Toggle status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: (payload: { id: string; status: 'ACTIVE' | 'INACTIVE' }) =>
      codingAdminService.updateProblem(payload.id, { status: payload.status }),
    onSuccess: () => {
      toast.success('Problem status updated');
      queryClient.invalidateQueries({ queryKey: ['cohort-coding-problems', cohortId] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to update status');
    },
  });

  // Revoke student submission mutation
  const revokeSubmissionMutation = useMutation({
    mutationFn: (submissionId: string) => codingAdminService.revokeSubmission(submissionId),
    onSuccess: (res) => {
      toast.success(res.data?.message || 'Submission revoked successfully. Student can now re-submit.');
      queryClient.invalidateQueries({ queryKey: ['problem-submissions', viewingSubmissionsProblem?.id] });
      queryClient.invalidateQueries({ queryKey: ['cohort-coding-problems', cohortId] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to revoke submission');
    },
  });

  // Fetch submissions for modal
  const { data: submissionsRes, isLoading: isLoadingSubmissions } = useQuery({
    queryKey: ['problem-submissions', viewingSubmissionsProblem?.id],
    queryFn: () => codingAdminService.getProblemSubmissions(viewingSubmissionsProblem!.id),
    enabled: !!viewingSubmissionsProblem,
  });
  const submissions: any[] = submissionsRes?.data?.data || [];

  // Filtered submissions in modal
  const filteredSubmissions = useMemo(() => {
    return submissions.filter((sub) => {
      const studentName = `${sub.user?.first_name || ''} ${sub.user?.last_name || ''}`.toLowerCase();
      const email = (sub.user?.email || '').toLowerCase();
      const query = submissionSearch.toLowerCase();
      const matchesQuery = studentName.includes(query) || email.includes(query);

      const matchesStatus =
        submissionStatusFilter === 'ALL' || sub.status === submissionStatusFilter;

      return matchesQuery && matchesStatus;
    });
  }, [submissions, submissionSearch, submissionStatusFilter]);

  // Submission analytics
  const totalSubs = submissions.length;
  const acceptedSubs = submissions.filter((s) => s.status === 'ACCEPTED').length;
  const passRate = totalSubs > 0 ? Math.round((acceptedSubs / totalSubs) * 100) : 0;
  const uniqueStudents = new Set(submissions.map((s) => s.user_id)).size;
  const avgRuntime =
    totalSubs > 0
      ? Math.round(
          submissions.reduce((acc, s) => acc + (s.execution_time_ms || 0), 0) / totalSubs,
        )
      : 0;

  const handleDelete = (problem: CodingProblemAdminData) => {
    if (window.confirm(`Are you sure you want to delete "${problem.title}"?`)) {
      deleteMutation.mutate(problem.id);
    }
  };

  const handleCopyCode = (id: string, code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCodeId(id);
    toast.success('Submitted solution copied to clipboard');
    setTimeout(() => setCopiedCodeId(null), 2000);
  };

  const getDifficultyBadge = (difficulty: string) => {
    switch (difficulty?.toUpperCase()) {
      case 'EASY':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center whitespace-nowrap">
            Easy
          </span>
        );
      case 'MEDIUM':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center whitespace-nowrap">
            Medium
          </span>
        );
      case 'HARD':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center whitespace-nowrap">
            Hard
          </span>
        );
      default:
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-gray-50 text-gray-700 border border-gray-200 inline-flex items-center whitespace-nowrap">
            {difficulty}
          </span>
        );
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACCEPTED':
        return (
          <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 size={12} />
            Accepted
          </span>
        );
      case 'WRONG_ANSWER':
        return (
          <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
            <XCircle size={12} />
            Wrong Answer
          </span>
        );
      case 'RUNTIME_ERROR':
        return (
          <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
            <AlertTriangle size={12} />
            Runtime Error
          </span>
        );
      case 'TIME_LIMIT_EXCEEDED':
        return (
          <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            <Clock size={12} />
            Time Limit
          </span>
        );
      default:
        return (
          <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
            {status}
          </span>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const easyCount = problems.filter((p) => p.difficulty === 'EASY').length;
  const mediumCount = problems.filter((p) => p.difficulty === 'MEDIUM').length;
  const hardCount = problems.filter((p) => p.difficulty === 'HARD').length;
  const totalSubmissions = problems.reduce((sum, p) => sum + (p.submissions_count || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header & Stats Cards */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-navy flex items-center gap-2">
            <Code2 className="text-primary" />
            Cohort Week-wise Coding Tasks
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Define Python coding problems organized by Week and Day for enrolled students, and analyze their submitted solutions.
          </p>
        </div>

        <Button
          type="button"
          variant="primary"
          onClick={() => {
            setEditingProblem(null);
            setIsCreateOpen(true);
          }}
          className="flex items-center gap-1.5 text-xs py-2 px-4 shadow-sm cursor-pointer"
        >
          <Plus size={16} />
          Add Coding Task
        </Button>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-border shadow-xs">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Tasks</span>
          <span className="text-2xl font-black text-navy mt-1 block">{problems.length}</span>
        </div>
        <div className="bg-white p-4 rounded-xl border border-border shadow-xs">
          <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider block">Easy / Medium / Hard</span>
          <span className="text-lg font-bold text-navy mt-1 block">
            <span className="text-emerald-600">{easyCount}</span> / <span className="text-amber-600">{mediumCount}</span> / <span className="text-rose-600">{hardCount}</span>
          </span>
        </div>
        <div className="bg-white p-4 rounded-xl border border-border shadow-xs">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Submissions</span>
          <span className="text-2xl font-black text-primary mt-1 block">{totalSubmissions}</span>
        </div>
        <div className="bg-white p-4 rounded-xl border border-border shadow-xs">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Active Tasks</span>
          <span className="text-sm font-bold text-emerald-700 mt-2 block flex items-center gap-1">
            <CheckCircle size={14} />
            {problems.filter((p) => p.status === 'ACTIVE').length} Published
          </span>
        </div>
      </div>

      {/* Week Selector Tabs */}
      <div className="flex border-b border-gray-200 gap-2 bg-gray-50/50 p-1.5 rounded-xl flex-wrap">
        <button
          type="button"
          onClick={() => setSelectedWeek('ALL')}
          className={`px-4 py-2 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center gap-2 ${
            selectedWeek === 'ALL'
              ? 'bg-white text-primary shadow-xs border border-gray-200'
              : 'text-slate-500 hover:text-navy hover:bg-gray-100'
          }`}
        >
          <Layers size={14} />
          All Weeks
          <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-gray-200 text-slate-600">
            {problems.length}
          </span>
        </button>

        {(activeWeeks.length > 0 ? activeWeeks : [1, 2, 3, 4]).map((wNum) => {
          const count = problems.filter((p) => (p.week_number || 1) === wNum).length;
          const isSelected = selectedWeek === wNum;
          return (
            <button
              key={wNum}
              type="button"
              onClick={() => setSelectedWeek(wNum)}
              className={`px-4 py-2 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center gap-2 ${
                isSelected
                  ? 'bg-white text-primary shadow-xs border border-gray-200'
                  : 'text-slate-500 hover:text-navy hover:bg-gray-100'
              }`}
            >
              <Calendar size={14} />
              Week {wNum}
              <span
                className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                  isSelected ? 'bg-primary/10 text-primary' : 'bg-gray-200 text-slate-600'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Problems List Table */}
      {filteredProblems.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-300 p-8">
          <Code2 size={40} className="mx-auto text-gray-300 mb-3" />
          <h3 className="text-base font-bold text-navy">
            {selectedWeek === 'ALL'
              ? 'No Coding Tasks Defined Yet'
              : `No Coding Tasks for Week ${selectedWeek}`}
          </h3>
          <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
            Add Python algorithm problems assigned to Week {selectedWeek === 'ALL' ? '1' : selectedWeek} for this cohort.
          </p>
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              setEditingProblem(null);
              setIsCreateOpen(true);
            }}
            className="mt-4 text-xs inline-flex items-center gap-1.5 cursor-pointer"
          >
            <Plus size={14} />
            Add Task to Week {selectedWeek === 'ALL' ? '1' : selectedWeek}
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-2xl shadow-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-gray-50/90 border-b border-border text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="px-5 py-3.5 min-w-[260px]">Title & Type</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">Difficulty</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">Schedule</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">Test Cases</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">Submissions</th>
                  <th className="px-4 py-3.5 whitespace-nowrap text-center">Visibility</th>
                  <th className="px-5 py-3.5 whitespace-nowrap text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredProblems.map((problem) => (
                  <tr key={problem.id} className="hover:bg-blue-bg/30 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-navy text-sm leading-snug">{problem.title}</span>
                          {problem.problem_type === 'NOTEBOOK' && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700 border border-purple-200 inline-flex items-center gap-1 whitespace-nowrap shrink-0">
                              📊 ML / Colab
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
                          <span className="font-mono text-[10px] text-gray-400">/{problem.slug}</span>
                          <span>•</span>
                          {problem.problem_type === 'NOTEBOOK' ? (
                            <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded text-[10px] font-semibold inline-flex items-center gap-1 whitespace-nowrap">
                              📁 {problem.datasets?.length || 0} Attached Datasets
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-[10px] font-semibold inline-flex items-center whitespace-nowrap">
                              {problem.problem_type === 'FUNCTION' ? `fn: ${problem.function_name || 'solution'}` : 'STDIN / STDOUT'}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap">{getDifficultyBadge(problem.difficulty)}</td>

                    <td className="px-4 py-4 whitespace-nowrap text-slate-600">
                      <span className="font-semibold px-2.5 py-1 bg-gray-100 text-slate-700 rounded-lg text-[11px] inline-flex items-center whitespace-nowrap">
                        Week {problem.week_number || 1}
                        {problem.day_number ? `, Day ${problem.day_number}` : ''}
                      </span>
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap">
                      {problem.problem_type === 'NOTEBOOK' ? (
                        <span className="inline-flex items-center gap-1 font-bold text-purple-700 bg-purple-50 border border-purple-200 px-2.5 py-1 rounded-lg text-[11px] whitespace-nowrap">
                          🧪 {problem.notebook_assertions?.length || 0} Assertions
                        </span>
                      ) : (
                        <div className="flex items-center gap-1.5 whitespace-nowrap">
                          <span className="font-bold text-navy text-sm">{problem.total_test_cases || 0}</span>
                          <span className="text-[11px] text-slate-500 font-medium whitespace-nowrap">
                            ({problem.public_test_cases_count || 0} public, {problem.hidden_test_cases_count || 0} hidden)
                          </span>
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => {
                          setViewingSubmissionsProblem(problem);
                          setSubmissionSearch('');
                          setSubmissionStatusFilter('ALL');
                          setExpandedSubmissionId(null);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-bg text-primary hover:bg-primary hover:text-white rounded-xl text-xs font-bold transition-colors cursor-pointer border border-primary/20 shadow-xs whitespace-nowrap"
                      >
                        <Users size={13} />
                        {problem.submissions_count || 0} Submissions
                      </button>
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <div className="flex justify-center">
                        <Toggle
                          active={problem.status === 'ACTIVE'}
                          onChange={(active) =>
                            toggleStatusMutation.mutate({
                              id: problem.id,
                              status: active ? 'ACTIVE' : 'INACTIVE',
                            })
                          }
                        />
                      </div>
                    </td>

                    <td className="px-5 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingProblem(problem);
                            setIsCreateOpen(true);
                          }}
                          className="p-1.5 text-slate-500 hover:text-primary hover:bg-blue-bg rounded-lg transition-colors cursor-pointer"
                          title="Edit Task"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(problem)}
                          className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                          title="Delete Task"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Problem Modal */}
      {isCreateOpen && (
        <CodingProblemModal
          isOpen={isCreateOpen}
          onClose={() => {
            setIsCreateOpen(false);
            setEditingProblem(null);
          }}
          cohortId={cohortId}
          problem={
            editingProblem ||
            (selectedWeek !== 'ALL'
              ? ({ week_number: selectedWeek } as any)
              : null)
          }
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['cohort-coding-problems', cohortId] });
          }}
        />
      )}

      {/* Comprehensive Student Submissions & Solution Analysis Modal */}
      {viewingSubmissionsProblem && (
        <Modal
          isOpen={!!viewingSubmissionsProblem}
          onClose={() => setViewingSubmissionsProblem(null)}
          title={`Submissions & Solutions: ${viewingSubmissionsProblem.title}`}
          maxWidth="max-w-[950px]"
        >
          <div className="p-6 overflow-y-auto max-h-[78vh] space-y-5">
            {/* Top Analytics Summary Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3.5 bg-slate-50 border border-gray-200 rounded-xl">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                  Total Submissions
                </span>
                <span className="text-xl font-black text-navy mt-0.5 block">{totalSubs}</span>
              </div>
              <div className="p-3.5 bg-emerald-50/50 border border-emerald-200 rounded-xl">
                <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider block">
                  Pass Rate
                </span>
                <span className="text-xl font-black text-emerald-700 mt-0.5 block">{passRate}%</span>
                <span className="text-[10px] text-emerald-600 font-semibold">{acceptedSubs} Accepted</span>
              </div>
              <div className="p-3.5 bg-blue-bg/60 border border-primary/20 rounded-xl">
                <span className="text-[10px] font-bold text-primary uppercase tracking-wider block">
                  Unique Students
                </span>
                <span className="text-xl font-black text-primary mt-0.5 block">{uniqueStudents}</span>
              </div>
              <div className="p-3.5 bg-slate-50 border border-gray-200 rounded-xl">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                  Avg Runtime
                </span>
                <span className="text-xl font-black text-navy mt-0.5 block">{avgRuntime}ms</span>
              </div>
            </div>

            {/* Filter and Search Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50 p-3 rounded-xl border border-gray-200">
              <div className="relative flex-1">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter by student name or email..."
                  value={submissionSearch}
                  onChange={(e) => setSubmissionSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div className="flex items-center gap-1 bg-white p-1 rounded-lg border border-gray-200 text-xs shrink-0">
                {(['ALL', 'ACCEPTED', 'WRONG_ANSWER', 'RUNTIME_ERROR'] as const).map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setSubmissionStatusFilter(status)}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-colors cursor-pointer ${
                      submissionStatusFilter === status
                        ? 'bg-primary text-white'
                        : 'text-slate-600 hover:text-navy hover:bg-gray-100'
                    }`}
                  >
                    {status === 'ALL' ? 'All' : status.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>

            {/* Submissions List */}
            {isLoadingSubmissions ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-xs font-semibold bg-gray-50 rounded-xl border border-dashed border-gray-200">
                {submissionSearch || submissionStatusFilter !== 'ALL'
                  ? 'No submissions match your filter criteria.'
                  : 'No student submissions recorded yet for this coding task.'}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredSubmissions.map((sub: any) => {
                  const isExpanded = expandedSubmissionId === sub.id;
                  const isCopied = copiedCodeId === sub.id;

                  return (
                    <div
                      key={sub.id}
                      className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-xs transition-all"
                    >
                      {/* Submission Header Card */}
                      <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white hover:bg-gray-50/70 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-blue-bg text-primary font-bold flex items-center justify-center text-xs shrink-0 border border-primary/20">
                            {sub.user?.first_name?.[0]?.toUpperCase() || 'S'}
                          </div>
                          <div>
                            <h4 className="font-bold text-navy text-xs">
                              {sub.user?.first_name} {sub.user?.last_name}
                            </h4>
                            <span className="text-[11px] text-gray-500">{sub.user?.email}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 self-end sm:self-center">
                          {getStatusBadge(sub.status)}

                          <div className="text-right text-xs">
                            <span className="font-bold text-slate-700 block">
                              {sub.passed_tests}/{sub.total_tests} Tests Passed
                            </span>
                            <span className="text-[10px] text-slate-400">
                              {new Date(sub.submitted_at).toLocaleString()}
                            </span>
                          </div>

                          <button
                            type="button"
                            disabled={revokeSubmissionMutation.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              const studentName = `${sub.user?.first_name || 'Student'} ${sub.user?.last_name || ''}`.trim();
                              if (window.confirm(`Revoke ${studentName}'s submission for "${viewingSubmissionsProblem.title}"?\n\nThis will remove the current submission and allow the student to write code and submit again.`)) {
                                revokeSubmissionMutation.mutate(sub.id);
                              }
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-rose-600 hover:text-white bg-rose-50 hover:bg-rose-600 border border-rose-200 hover:border-rose-600 rounded-lg transition-all cursor-pointer shadow-xs disabled:opacity-50"
                            title="Revoke submission (allow student to re-submit)"
                          >
                            <RotateCcw size={12} />
                            <span>Revoke</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => setExpandedSubmissionId(isExpanded ? null : sub.id)}
                            className="p-1.5 text-slate-500 hover:text-primary hover:bg-blue-bg rounded-lg transition-colors cursor-pointer"
                            title={isExpanded ? 'Hide Solution' : 'Inspect Solution'}
                          >
                            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                          </button>
                        </div>
                      </div>

                      {/* Solution Inspector Drawer */}
                      {isExpanded && (
                        <div className="p-4 bg-slate-900 border-t border-slate-800 text-slate-200 space-y-4">
                          {/* Solution Top Bar */}
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-emerald-400 font-bold text-xs flex items-center gap-1.5">
                              <Terminal size={14} />
                              Submitted Python Solution
                              {sub.execution_time_ms != null && (
                                <span className="text-slate-400 text-[10px] font-sans font-normal ml-2">
                                  Runtime: {Math.round(sub.execution_time_ms)}ms
                                </span>
                              )}
                            </span>

                            <button
                              type="button"
                              onClick={() => handleCopyCode(sub.id, sub.source_code)}
                              className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-300 flex items-center gap-1 transition-colors cursor-pointer"
                            >
                              {isCopied ? (
                                <>
                                  <Check size={12} className="text-emerald-400" />
                                  Copied!
                                </>
                              ) : (
                                <>
                                  <Copy size={12} />
                                  Copy Code
                                </>
                              )}
                            </button>
                          </div>

                          {/* Code Display */}
                          <pre className="p-3 bg-slate-950 text-emerald-400 font-mono text-xs rounded-xl overflow-x-auto border border-slate-800 whitespace-pre-wrap leading-relaxed">
                            {sub.source_code}
                          </pre>

                          {/* Error Message */}
                          {sub.error_message && (
                            <div className="p-3 bg-red-950/60 border border-red-800/60 rounded-xl text-xs space-y-1">
                              <span className="font-bold text-red-400 flex items-center gap-1">
                                <AlertTriangle size={13} />
                                Execution Error / Traceback
                              </span>
                              <pre className="font-mono text-red-300 text-[11px] whitespace-pre-wrap overflow-x-auto">
                                {sub.error_message}
                              </pre>
                            </div>
                          )}

                          {/* Test Cases Results Breakdown */}
                          {sub.test_results && sub.test_results.length > 0 && (
                            <div className="space-y-2 pt-2 border-t border-slate-800">
                              <span className="text-xs font-bold text-slate-300 block">
                                Test Cases Breakdown ({sub.test_results.length})
                              </span>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {sub.test_results.map((tr: any, idx: number) => {
                                  const isPassed = tr.status?.toLowerCase() === 'passed';
                                  return (
                                    <div
                                      key={tr.id || tr.test_case_id || idx}
                                      className={`p-2.5 rounded-lg border text-xs flex items-center justify-between ${
                                        isPassed
                                          ? 'bg-emerald-950/40 border-emerald-800/50 text-emerald-300'
                                          : 'bg-rose-950/40 border-rose-800/50 text-rose-300'
                                      }`}
                                    >
                                      <span className="flex items-center gap-1.5 font-bold">
                                        {isPassed ? (
                                          <CheckCircle2 size={13} className="text-emerald-400" />
                                        ) : (
                                          <XCircle size={13} className="text-rose-400" />
                                        )}
                                        Test #{tr.order_no || idx + 1} {tr.is_hidden ? '(Hidden)' : ''}
                                      </span>
                                      <span className="font-mono text-[10px] text-slate-400">
                                        {Math.round(tr.execution_time_ms || 0)}ms
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CodingTasksTab;
